import { memo } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import MLBPlayerAvatar, { positionColors } from '@/components/mlb-beta/MLBPlayerAvatar';
import ProbablePitcherBadge from '@/components/mlb-beta/ProbablePitcherBadge';
import { formatPositionDisplay } from '@/lib/positionEligibility';
import { cn } from '@/lib/utils';
import type { WaiverMode } from '@/components/mlb-beta/AddDropDialog';
import type { SeasonStats, StatColumn } from '@/components/mlb-beta/statsTableDefs';
import type { PitcherStartInfo } from '@/hooks/useProbablePitchers';
import { StatStrip } from './StatStrip';
import { PlayerActionMobile, type AddDropApi } from './PlayerActionMobile';
import { getSPTSLabel, type ScoringMode } from '../playersMobileStats';

export interface GameInfo {
  opponent: string;
  isHome: boolean;
  status: string;
}

export interface PlayerCardData {
  id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: string[] | null;
  mlb_team_abbreviation: string | null;
  mlb_id: number | null;
  status: string;
  headshot_url: string | null;
  stats: SeasonStats | null;
}

interface PlayerCardMobileProps {
  player: PlayerCardData;
  rank: number;
  spts: number;
  scoringMode: ScoringMode;
  columns: StatColumn[];
  statView: 'season' | 'pergame';
  gamesPlayed: number;
  gameInfo: GameInfo | undefined;
  pitcherStartInfo: PitcherStartInfo | null;
  isProbablePitcher: boolean;
  compareMode: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  addDrop: AddDropApi;
  waiverMode: WaiverMode;
  onCardClick: () => void;
  onToggleSelected: () => void;
  onToggleExpanded: () => void;
  onAddClick: (p: PlayerCardData) => void;
  onDropClick: (id: string) => void;
  onTradeClick: () => void;
}

const INJURY_SHORT: Record<string, string> = {
  'Injured 7-Day': 'IL-7',
  'Injured 10-Day': 'IL-10',
  'Injured 15-Day': 'IL-15',
  'Injured 60-Day': 'IL-60',
  'Injured - Full Season': 'IL-FS',
  'Day-To-Day': 'DTD',
};

function InjuryPill({ status }: { status: string }) {
  const short = INJURY_SHORT[status];
  if (!short) return null;
  const isDTD = short === 'DTD';
  return (
    <Badge
      variant="outline"
      className={cn(
        'text-[9px] px-1.5 h-4 shrink-0 font-semibold',
        isDTD
          ? 'text-gold bg-gold/10 border-gold/35'
          : 'text-damage bg-damage/10 border-damage/35',
      )}
    >
      {short}
    </Badge>
  );
}

function MatchupChip({ gameInfo }: { gameInfo: GameInfo | undefined }) {
  if (!gameInfo) return null;
  if (gameInfo.status === 'live' || gameInfo.status === 'In Progress') {
    return (
      <Badge
        variant="outline"
        className="text-[9px] px-1.5 h-4 bg-primary text-primary-foreground border-primary animate-pulse font-semibold shrink-0"
      >
        ● LIVE
      </Badge>
    );
  }
  const isFinal = gameInfo.status === 'Final' || gameInfo.status === 'Game Over';
  return (
    <span className={cn('text-[10px]', isFinal ? 'text-muted-foreground/60' : 'text-muted-foreground')}>
      {isFinal ? 'Final' : `${gameInfo.isHome ? 'vs' : '@'} ${gameInfo.opponent}`}
    </span>
  );
}

function OwnershipBadge({ playerId, addDrop }: { playerId: string; addDrop: AddDropApi }) {
  if (addDrop.isOnMyTeam(playerId)) {
    return (
      <Badge
        variant="secondary"
        className="text-[9px] px-1.5 h-4 bg-primary/15 text-primary border-primary/20 shrink-0"
      >
        My Team
      </Badge>
    );
  }
  const owner = addDrop.getOwnerTeamName(playerId);
  if (owner) {
    return (
      <Badge variant="outline" className="text-[9px] px-1.5 h-4 text-muted-foreground shrink-0">
        {owner}
      </Badge>
    );
  }
  if (addDrop.isOnWaiverWire(playerId)) {
    return (
      <Badge
        variant="outline"
        className="text-[9px] px-1.5 h-4 text-amber-400 border-amber-400/30 shrink-0"
      >
        WW
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[9px] px-1.5 h-4 text-success border-success/30 shrink-0">
      FA
    </Badge>
  );
}

function RankCoin({ rank }: { rank: number }) {
  let cls = 'text-muted-foreground/60';
  if (rank <= 3) cls = 'text-gold font-extrabold';
  else if (rank <= 10) cls = 'text-primary font-bold';
  else if (rank <= 50) cls = 'text-muted-foreground font-semibold';
  return (
    <span className={cn('w-7 text-right font-display tabular-nums text-[13px]', cls)}>
      #{rank}
    </span>
  );
}

function PlayerCardMobileImpl({
  player,
  rank,
  spts,
  scoringMode,
  columns,
  statView,
  gamesPlayed,
  gameInfo,
  pitcherStartInfo,
  isProbablePitcher,
  compareMode,
  isSelected,
  isExpanded,
  addDrop,
  waiverMode,
  onCardClick,
  onToggleSelected,
  onToggleExpanded,
  onAddClick,
  onDropClick,
  onTradeClick,
}: PlayerCardMobileProps) {
  const isLive = gameInfo?.status === 'live' || gameInfo?.status === 'In Progress';
  const isTopTen = rank <= 10;
  const isPerGame = statView === 'pergame';
  const sptsValue = isPerGame && gamesPlayed > 0 ? spts / gamesPlayed : spts;

  const onClick = () => {
    if (compareMode) onToggleSelected();
    else onCardClick();
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-card border rounded-xl overflow-hidden transition-colors cursor-pointer',
        'border-border',
        isSelected && 'bg-primary/10 border-primary/50',
        isLive && 'shadow-sabotage',
      )}
    >
      <div className="flex items-center gap-2 px-2.5 py-1.5 min-h-[52px]">
        {compareMode && (
          <div onClick={(e) => e.stopPropagation()} className="shrink-0">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onToggleSelected}
              aria-label={`Select ${player.full_name} for compare`}
              className="h-[22px] w-[22px]"
            />
          </div>
        )}

        <RankCoin rank={rank} />

        <MLBPlayerAvatar
          name={player.full_name}
          headshotUrl={player.headshot_url}
          position={player.position_abbreviation}
          size="sm"
          className="w-9 h-9 text-[10px]"
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-[14.5px] truncate text-foreground">
              {player.full_name}
            </span>
            <InjuryPill status={player.status} />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <Badge
              variant="outline"
              className={cn('text-[9.5px] px-1 py-0 h-3.5 shrink-0', positionColors[player.position_abbreviation])}
            >
              {formatPositionDisplay(player.position_abbreviation, player.position_eligibility)}
            </Badge>
            <span className="text-[10.5px] font-display font-semibold text-muted-foreground shrink-0">
              {player.mlb_team_abbreviation || 'FA'}
            </span>
            <MatchupChip gameInfo={gameInfo} />
            <ProbablePitcherBadge startInfo={pitcherStartInfo} compact />
            {isProbablePitcher && (
              <Badge
                variant="outline"
                className="text-[8px] px-1 py-0 h-3.5 border-orange-400/60 text-orange-500 bg-orange-500/10 font-semibold shrink-0"
              >
                PP
              </Badge>
            )}
            <OwnershipBadge playerId={player.id} addDrop={addDrop} />
          </div>
        </div>

        <div className="flex flex-col items-end shrink-0">
          <span
            className={cn(
              'font-display font-extrabold text-[18px] text-primary tabular-nums leading-none',
              isTopTen && 'drop-shadow-[0_0_12px_hsl(25_95%_53%/0.45)]',
            )}
          >
            {sptsValue.toFixed(1)}
          </span>
          <span className="text-[8.5px] uppercase tracking-wider text-muted-foreground font-display font-semibold mt-0.5">
            {getSPTSLabel(scoringMode)}{isPerGame ? '/G' : ''}
          </span>
          <div className="mt-0.5" onClick={(e) => e.stopPropagation()}>
            <PlayerActionMobile
              player={player}
              addDrop={addDrop}
              waiverMode={waiverMode}
              onAddClick={onAddClick}
              onDropClick={onDropClick}
              onTradeClick={onTradeClick}
            />
          </div>
        </div>
      </div>

      {isExpanded && (
        <StatStrip
          columns={columns}
          stats={player.stats}
          statView={statView}
          gamesPlayed={gamesPlayed}
        />
      )}

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleExpanded(); }}
        aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
        aria-expanded={isExpanded}
        className="w-full flex items-center justify-center py-0.5 border-t border-border/40 hover:bg-muted/20"
      >
        <ChevronDown
          className={cn(
            'w-4 h-4 text-muted-foreground transition-transform duration-150',
            isExpanded && 'rotate-180',
          )}
        />
      </button>
    </div>
  );
}

export const PlayerCardMobile = memo(PlayerCardMobileImpl);
export default PlayerCardMobile;
